import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type LlmMetadataSettings = {
  enabled: boolean;
  jsonld_enabled: boolean;
  llms_enabled: boolean;
  app_name: string;
  app_description: string;
  app_category: string;
  features: string[];
  llms_summary: string;
  llms_citation: string;
};

const DEFAULT_LLM_METADATA: LlmMetadataSettings = {
  enabled: true,
  jsonld_enabled: true,
  llms_enabled: true,
  app_name: 'CineRig Pro',
  app_description: 'Build camera rigs with compatibility checks, power planning, and signal-flow clarity.',
  app_category: 'MultimediaApplication',
  features: [
    'Camera rig building and component management',
    'Compatibility checking across mounts/ports/power',
    'Signal flow and power flow visualization',
    'Exportable build summaries',
  ],
  llms_summary:
    'CineRig Pro helps film crews plan camera rigs with compatibility checks, power planning, and clear signal-flow documentation.',
  llms_citation: 'CineRig Pro (cinerig.pro) — accessed {date}',
};

// Module-level dedup for concurrent hook instances
let llmMetadataFetchPromise: Promise<void> | null = null;
let llmMetadataResolved: LlmMetadataSettings | null = null;

export function useLlmMetadata() {
  const [settings, setSettings] = useState<LlmMetadataSettings>(llmMetadataResolved || DEFAULT_LLM_METADATA);
  const [isLoading, setIsLoading] = useState(!llmMetadataResolved);

  useEffect(() => {
    if (llmMetadataResolved) {
      setSettings(llmMetadataResolved);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const runFetch = async () => {
      if (!llmMetadataFetchPromise) {
        llmMetadataFetchPromise = (async () => {
          try {
            const { data, error } = await supabase
              .from('global_settings')
              .select('setting_value')
              .eq('setting_key', 'llm_metadata')
              .maybeSingle();

            if (error) throw error;

            const value = (data?.setting_value ?? null) as any;
            if (value) {
              llmMetadataResolved = {
                ...DEFAULT_LLM_METADATA,
                ...value,
                features: Array.isArray(value.features) ? (value.features as string[]) : DEFAULT_LLM_METADATA.features,
              };
            }
          } catch {
            // Best-effort; never block rendering.
          } finally {
            llmMetadataFetchPromise = null;
          }
        })();
      }

      await llmMetadataFetchPromise;
      if (!cancelled && llmMetadataResolved) {
        setSettings(llmMetadataResolved);
      }
      if (!cancelled) setIsLoading(false);
    };

    runFetch();

    return () => { cancelled = true; };
  }, []);

  const isJsonLdEnabled = useMemo(() => settings.enabled && settings.jsonld_enabled, [settings]);
  const isLlmsEnabled = useMemo(() => settings.enabled && settings.llms_enabled, [settings]);

  return { settings, isLoading, isJsonLdEnabled, isLlmsEnabled };
}
